import { useAppForm } from '@/lib/form';
import { Turnstile } from '@marsidev/react-turnstile';
import { useStore } from '@tanstack/react-form';
import { type VariantProps } from 'class-variance-authority';
import { useEffect, useState } from 'react';
import { z } from 'zod';
import { schemaFieldVariants } from './constants';
import { SchemaFormProvider } from './contexts';
import { SchemaFormFields } from './SchemaFormFields';

const TURNSTILE_SITE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY as string;

export type SchemaFormValues = Record<string, unknown> & {
  turnstileToken?: string | null;
};

interface SchemaFormProps extends VariantProps<typeof schemaFieldVariants> {
  zodSchema: z.ZodObject<z.ZodRawShape>;
  displaySchema?: z.ZodObject<z.ZodRawShape>;
  onSubmit: (values: SchemaFormValues) => Promise<void>;
  defaultValuesCallback?: () => Record<string, unknown>;
  enableTurnstile?: boolean;
  template: React.ReactNode;
  className?: string;
}

export function SchemaForm({
  zodSchema,
  displaySchema,
  onSubmit,
  defaultValuesCallback,
  enableTurnstile = false,
  variant,
  template,
  className,
}: SchemaFormProps) {
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [submitFailed, setSubmitFailed] = useState(false);

  const defaultValues = defaultValuesCallback
    ? defaultValuesCallback()
    : ({} as Record<string, unknown>);

  const form = useAppForm({
    defaultValues,
    validators: { onSubmit: zodSchema, onBlur: zodSchema } as any,
    onSubmit: async ({ value }) => {
      try {
        setSubmitFailed(false);
        await onSubmit({ ...value, turnstileToken });
      } catch {
        setSubmitFailed(true);
      }
    },
  });

  const isSubmitting = useStore(form.store, (state) => state.isSubmitting);
  const isSubmitted = useStore(form.store, (state) => state.isSubmitted);
  const isDirty = useStore(form.store, (state) => state.isDirty);

  useEffect(() => {
    if (!editMode && defaultValuesCallback) {
      const defaults = defaultValuesCallback();

      for (const [key, value] of Object.entries(defaults)) {
        if (value === undefined || value === '') continue;

        const current = form.getFieldValue(key);
        if (current === undefined || current === null || current === '') {
          form.setFieldValue(key, value);
        }
      }
    }
  }, [defaultValuesCallback]);

  const renderFields = (editMode: boolean) => (
    <>
      <SchemaFormFields
        form={form}
        editMode={editMode}
        isLoading={false}
        disabled={isSubmitting}
        zodSchema={displaySchema ?? zodSchema}
        variant={variant}
      />
      {enableTurnstile && (
        <Turnstile
          siteKey={TURNSTILE_SITE_KEY}
          onSuccess={setTurnstileToken}
          options={{ appearance: 'interaction-only' }}
        />
      )}
    </>
  );

  return (
    <SchemaFormProvider
      value={{
        renderFields,
        isSubmitting,
        isSubmitted,
        submitFailed,
        isDirty,
        resetForm: () => form.reset(),
        editMode,
        enableEditMode: () => setEditMode(true),
        disableEditMode: () => setEditMode(false),
      }}
    >
      <form
        className={className}
        onSubmit={async (e) => {
          e.preventDefault();
          await form.validate('submit');
          await form.handleSubmit();
        }}
      >
        {template}
      </form>
    </SchemaFormProvider>
  );
}
